import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const QuestionCard = ({ 
  question, 
  onAnswerSubmit, 
  onBookmark, 
  isBookmarked,
  showExplanation,
  selectedAnswer,
  isSubmitted 
}) => {
  const [localSelectedAnswer, setLocalSelectedAnswer] = useState(selectedAnswer || null);

  const handleAnswerSelect = (optionId) => {
    if (!isSubmitted) {
      setLocalSelectedAnswer(optionId);
    }
  };

  const handleSubmit = () => {
    if (localSelectedAnswer) {
      onAnswerSubmit(localSelectedAnswer);
    }
  };

  const getOptionClassName = (optionId) => {
    const baseClasses = "w-full text-left p-4 rounded-lg border-2 transition-all duration-200";
    
    if (!isSubmitted) {
      return `${baseClasses} ${
        localSelectedAnswer === optionId
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border bg-card hover:border-primary/50 hover:bg-muted text-foreground'
      }`;
    }

    if (optionId === question?.correctAnswer) {
      return `${baseClasses} border-success bg-success/10 text-foreground`;
    }

    if (optionId === localSelectedAnswer && optionId !== question?.correctAnswer) {
      return `${baseClasses} border-error bg-error/10 text-foreground`;
    }

    return `${baseClasses} border-border bg-muted/50 text-muted-foreground`;
  };

  const getOptionIcon = (optionId) => {
    if (!isSubmitted) {
      return localSelectedAnswer === optionId ? 'CheckCircle2' : 'Circle';
    }

    if (optionId === question?.correctAnswer) {
      return 'CheckCircle2';
    }

    if (optionId === localSelectedAnswer && optionId !== question?.correctAnswer) {
      return 'XCircle';
    }

    return 'Circle';
  };

  const getOptionIconColor = (optionId) => {
    if (!isSubmitted) {
      return localSelectedAnswer === optionId ? 'var(--color-primary)' : 'var(--color-muted-foreground)';
    }

    if (optionId === question?.correctAnswer) {
      return 'var(--color-success)';
    }

    if (optionId === localSelectedAnswer && optionId !== question?.correctAnswer) {
      return 'var(--color-error)';
    }

    return 'var(--color-muted-foreground)';
  };

  return (
    <div className="bg-card rounded-xl shadow-elevation-2 p-6 md:p-8">
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-3">
            <span className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
              {question?.knowledgeArea}
            </span>
            <span className={`px-3 py-1 text-xs font-medium rounded-full ${
              question?.difficulty === 'beginner' ? 'bg-success/10 text-success' :
              question?.difficulty === 'intermediate'? 'bg-warning/10 text-warning' : 'bg-error/10 text-error'
            }`}>
              {question?.difficulty?.charAt(0)?.toUpperCase() + question?.difficulty?.slice(1)}
            </span>
          </div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-2">
            Question {question?.number}
          </h3>
        </div>
        <button
          onClick={onBookmark}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
          aria-label={isBookmarked ? "Remove bookmark" : "Add bookmark"}
        >
          <Icon 
            name={isBookmarked ? "Bookmark" : "BookmarkPlus"} 
            size={20} 
            color={isBookmarked ? "var(--color-warning)" : "var(--color-muted-foreground)"}
          />
        </button>
      </div>
      <div className="mb-6">
        <p className="text-sm md:text-base text-foreground leading-relaxed whitespace-pre-line">
          {question?.scenario}
        </p>
      </div>
      <div className="space-y-3 mb-6">
        {question?.options?.map((option) => (
          <button
            key={option?.id}
            onClick={() => handleAnswerSelect(option?.id)}
            disabled={isSubmitted}
            className={getOptionClassName(option?.id)}
          >
            <div className="flex items-start gap-3">
              <Icon 
                name={getOptionIcon(option?.id)} 
                size={20} 
                color={getOptionIconColor(option?.id)}
                className="flex-shrink-0 mt-0.5"
              />
              <span className="text-sm md:text-base flex-1 text-left">
                {option?.text}
              </span>
            </div>
          </button>
        ))}
      </div>
      {!isSubmitted && (
        <Button
          variant="default"
          fullWidth
          onClick={handleSubmit}
          disabled={!localSelectedAnswer}
          iconName="Send"
          iconPosition="right"
        >
          Submit Answer
        </Button>
      )}
      {isSubmitted && showExplanation && (
        <div className="mt-6 p-4 md:p-6 bg-muted/50 rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-3">
            <Icon name="Lightbulb" size={20} className="text-warning" />
            <h4 className="text-sm font-semibold text-foreground">Explanation</h4>
          </div>
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
            {question?.explanation}
          </p>
          <div className="mt-4 pt-4 border-t border-border">
            <p className="text-xs text-muted-foreground">
              <span className="font-medium">Reference:</span> {question?.reference}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;